// ═══ Theme System — The Gathering V3 ═══
// Derives a complete palette from a community's accent color.
// Internal users get GitHub dark. Community users get Deep Reef + accent.

export interface ThemePalette {
  // Backgrounds
  bgBody: string
  bgSurface: string
  bgElevated: string
  // Borders
  border: string
  borderAccent: string
  // Text
  textPrimary: string
  textSecondary: string
  textMuted: string
  // Accent
  accent: string
  accentHover: string
  accentMuted: string      // accent at 15% opacity — badges, subtle highlights
  accentBorder: string     // accent at 30% opacity — borders, rings
  accentGlow: string       // accent at 25% opacity — box shadows
  // Semantic
  danger: string
  warning: string
  success: string
  // Fonts
  fontDisplay: string
  fontBody: string
}

// Internal: GitHub dark palette
const INTERNAL_PALETTE: ThemePalette = {
  bgBody: '#0d1117',
  bgSurface: '#161b22',
  bgElevated: '#1c2129',
  border: '#30363d',
  borderAccent: '#484f58',
  textPrimary: '#e6edf3',
  textSecondary: '#8b949e',
  textMuted: '#6e7a88',
  accent: '#1B4965',
  accentHover: '#236080',
  accentMuted: 'rgba(27,73,101,0.15)',
  accentBorder: 'rgba(27,73,101,0.3)',
  accentGlow: 'rgba(27,73,101,0.25)',
  danger: '#f85149',
  warning: '#d29922',
  success: '#3fb950',
  fontDisplay: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontBody: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
}

// Community: Deep Reef base + derived accent
function communityPalette(accent: string, fontDisplay?: string | null): ThemePalette {
  return {
    bgBody: '#0A1628',
    bgSurface: '#111D2E',
    bgElevated: '#1A2940',
    border: '#1E3048',
    borderAccent: '#2A4060',
    textPrimary: '#F0F4F8',
    textSecondary: '#8b949e',
    textMuted: '#4A6080',
    accent,
    accentHover: accent + 'cc',
    accentMuted: accent + '26',        // ~15%
    accentBorder: accent + '4d',        // ~30%
    accentGlow: accent + '40',          // ~25%
    danger: '#f85149',
    warning: '#d29922',
    success: '#3fb950',
    fontDisplay: fontDisplay
      ? `'${fontDisplay}', 'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif`
      : "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif",
    fontBody: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  }
}

export function buildPalette(
  isCommunity: boolean,
  accent?: string,
  fontDisplay?: string | null,
): ThemePalette {
  if (!isCommunity) return INTERNAL_PALETTE
  return communityPalette(accent || '#FF9B21', fontDisplay)
}

// Apply palette as CSS custom properties on :root
export function applyPalette(palette: ThemePalette) {
  const root = document.documentElement.style
  root.setProperty('--bg-body', palette.bgBody)
  root.setProperty('--bg-surface', palette.bgSurface)
  root.setProperty('--bg-elevated', palette.bgElevated)
  root.setProperty('--border', palette.border)
  root.setProperty('--border-accent', palette.borderAccent)
  root.setProperty('--text-primary', palette.textPrimary)
  root.setProperty('--text-secondary', palette.textSecondary)
  root.setProperty('--text-muted', palette.textMuted)
  root.setProperty('--accent', palette.accent)
  root.setProperty('--accent-hover', palette.accentHover)
  root.setProperty('--accent-muted', palette.accentMuted)
  root.setProperty('--accent-border', palette.accentBorder)
  root.setProperty('--accent-glow', palette.accentGlow)
  root.setProperty('--danger', palette.danger)
  root.setProperty('--warning', palette.warning)
  root.setProperty('--success', palette.success)
  root.setProperty('--font-display', palette.fontDisplay)
  root.setProperty('--font-body', palette.fontBody)

  // Also set the body background directly to prevent flash
  document.body.style.background = palette.bgBody
  document.body.style.color = palette.textPrimary
}

// Load Google Fonts for community users
let fontsLoaded = false
export function loadCommunityFonts(customFont?: string | null) {
  if (fontsLoaded) return
  fontsLoaded = true
  const families = [
    'Plus+Jakarta+Sans:wght@400;500;600;700',
    'Inter:wght@400;500;600',
  ]
  if (customFont && customFont !== 'Plus Jakarta Sans' && customFont !== 'Inter') {
    families.push(`${customFont.replace(/ /g, '+')}:wght@400;500;600;700`)
  }
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = 'https://fonts.googleapis.com/css2?' + families.map(f => 'family=' + f).join('&') + '&display=swap'
  document.head.appendChild(link)
}
